import { Part, PartCore } from 'types/part';
import { StockLotCRUDSchema } from 'types/inventory';
import {
  Box,
  Chip,
  GridLegacy,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PartAvatar from 'ui-component/extended/Avatars/PartAvatar';
import CopyClipboard from 'ui-component/extended/CopyClipboard';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { ALL_APPS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { useSelector } from 'store';
import { useMemo } from 'react';
import { UUID } from 'types/api';
import { removeNonAlphanumericOrPeriod } from 'utils/functions';
import _ from 'lodash';
import { IconInfoCircle } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import {
  OrgPartSchema,
  OrgPartSchemaLite,
  PublicPartSchemaSearch,
} from 'types/clientV2/parts';
import reactStringReplace from 'react-string-replace';

type PartCellProps = {
  part:
    | Part
    | PartCore
    | PublicPartSchemaSearch
    | OrgPartSchema
    | OrgPartSchemaLite
    | null;
  noPart?: string | null;
  altCount?: number | null;
  stockLot?: StockLotCRUDSchema | null | StockLotCRUDSchema['id'];
  searchParams?: string;
  unapprovedAltCount?: number | null;
  v2PartUUID?: UUID | null;
  uploadedMpns?: string[] | null;
  uploadedMfgs?: string[] | null;
};
const PartCell = ({
  part,
  noPart,
  altCount,
  stockLot,
  searchParams,
  unapprovedAltCount,
  v2PartUUID,
  uploadedMpns,
  uploadedMfgs,
}: PartCellProps) => {
  const theme = useTheme();
  const { isPermissionDisabled } = useAppAccessContext();
  const customPnsEnabled = !isPermissionDisabled({
    app: ALL_APPS.PART_LIBRARY.id,
    feature: ALL_FEATURE_IDS.CUSTOM_PNS,
  });
  const { activeOrg } = useSelector((state) => state.org);

  const partMpn = part?.mpn;
  const manufacturer = _.get(part, 'mfg') ?? _.get(part, 'manufacturer');
  const customId = _.get(part, 'customId');
  const description = part?.description;
  const aliasMpns = _.get(part, 'aliasMpns') ?? _.get(part, 'altMpns') ?? [];
  const aliasMfgs = _.get(part, 'aliasMfgs') ?? _.get(part, 'mfgAliases') ?? [];
  const revision = _.get(part, 'revision');

  const splitSearchString = useMemo(
    () => _.chain(searchParams).split(' ').compact().value(),
    [searchParams]
  );

  const searchStringAltMatches = _.filter(aliasMpns, (a) =>
    _.some(splitSearchString, (s) =>
      _.includes(
        removeNonAlphanumericOrPeriod(a)?.toLowerCase(),
        removeNonAlphanumericOrPeriod(s)?.toLowerCase()
      )
    )
  );

  const searchHighlightString = (input: string) => {
    if (!searchParams) return input;
    const escapedSplitSearchString = _.map(splitSearchString, (s) =>
      _.replace(s, /[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const highlightRegex = _.join(escapedSplitSearchString, '|');
    return reactStringReplace(
      input,
      new RegExp(`(${highlightRegex})`, 'i'),
      (match, i) => (
        <span
          key={i}
          style={{ color: theme.palette.success.main, fontWeight: 'bold' }}
        >
          {match}
        </span>
      )
    );
  };

  const isAliasMfg = useMemo(
    () =>
      _.some(uploadedMfgs, (mfg) => {
        const cleanedMfg = removeNonAlphanumericOrPeriod(mfg)?.toLowerCase();
        const cleanedPartMfg =
          removeNonAlphanumericOrPeriod(manufacturer)?.toLowerCase();
        const cleanedPartMfgAliases = aliasMfgs?.map((alias) =>
          removeNonAlphanumericOrPeriod(alias)?.toLowerCase()
        );
        return (
          _.includes(cleanedPartMfgAliases, cleanedMfg) &&
          cleanedMfg !== cleanedPartMfg &&
          !_.includes(cleanedPartMfg, cleanedMfg)
        );
      }),
    [uploadedMfgs, manufacturer, aliasMfgs]
  );

  const isAliasMpn = useMemo(
    () =>
      _.some(uploadedMpns, (mpn) => {
        const cleanedMpn = removeNonAlphanumericOrPeriod(mpn)?.toLowerCase();
        const cleanedPartMpn =
          removeNonAlphanumericOrPeriod(partMpn)?.toLowerCase();
        const cleanedPartMpnAliases = aliasMpns?.map((alias) =>
          removeNonAlphanumericOrPeriod(alias)?.toLowerCase()
        );
        return (
          _.includes(cleanedPartMpnAliases, cleanedMpn) &&
          cleanedMpn !== cleanedPartMpn
        );
      }),
    [uploadedMpns, partMpn, aliasMpns]
  );

  return (
    <Box
      sx={{
        width: '100%',
        my: '4px',
      }}
    >
      <GridLegacy container columnSpacing={1.5} alignItems="center">
        <GridLegacy item xs="auto">
          <PartAvatar
            part={part}
            altCount={altCount}
            stockLot={stockLot}
            searchParams={searchParams}
            unapprovedAltCount={unapprovedAltCount}
            v2PartUUID={v2PartUUID}
          />
        </GridLegacy>
        <GridLegacy item xs sx={{ overflow: 'hidden' }}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography variant="body1">
              {noPart
                ? 'Unable to find match for:'
                : searchHighlightString(manufacturer || '-')}
            </Typography>
            {isAliasMfg && (
              <Tooltip
                title={`You specified ${_.first(
                  uploadedMfgs
                )}, which is another name for ${manufacturer}`}
              >
                <IconInfoCircle
                  width={20}
                  height={20}
                  color={theme.palette.success.main}
                />
              </Tooltip>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Tooltip
              title={
                aliasMpns && (aliasMpns || []).length > 0
                  ? `This part is also known as: ${aliasMpns.join(', ')}`
                  : undefined
              }
            >
              <Typography variant="subtitle1">
                {searchHighlightString(partMpn ?? noPart ?? '-')}
              </Typography>
            </Tooltip>
            {partMpn && (
              <CopyClipboard label="Part MPN" input={noPart || partMpn} />
            )}
            {isAliasMpn && (
              <Tooltip
                title={`You specified ${_.first(
                  uploadedMpns
                )}, which is another name for ${partMpn}`}
              >
                <IconInfoCircle
                  width={20}
                  height={20}
                  color={theme.palette.success.main}
                />
              </Tooltip>
            )}
          </Stack>
          {customPnsEnabled && customId && (
            <Typography variant="subtitle1">
              {searchHighlightString(customId)}
              <CopyClipboard
                label={`${activeOrg?.name || 'Custom'} Part Number`}
                input={customId}
              />
            </Typography>
          )}
          {revision && <Chip size="small" label={`Rev ${revision}`} />}
          {!!description && (
            <Tooltip title={description || ''}>
              <Typography noWrap variant="body1">
                {searchHighlightString(description ?? '')}
              </Typography>
            </Tooltip>
          )}
          {searchStringAltMatches.length > 0 &&
            !_.includes(
              partMpn?.toLowerCase(),
              searchParams?.toLowerCase()
            ) && (
              <Typography sx={{ fontStyle: 'italic' }} variant="caption">
                This part is also known as:{' '}
                {searchHighlightString(searchStringAltMatches.join(', '))}
              </Typography>
            )}
        </GridLegacy>
      </GridLegacy>
    </Box>
  );
};

export default PartCell;
